import { useMemo } from 'react';
import { Alert } from 'antd';

import { renderTemplate } from '../parsing';
import styled from 'styled-components';
import { ISystemVariable } from '../types';

export interface ITemplatePreviewProps {
  content: string;
  mockData: Record<string, unknown>;
  systemVariables?: ISystemVariable[];
  height?: number;
}

// Add system variables to the mock data
const TemplatePreview: React.FC<ITemplatePreviewProps> = ({
  content,
  mockData,
  systemVariables,
  height,
}) => {
  const { renderedContent, error } = useMemo(() => {
    const systemVariablesData = systemVariables?.reduce(
      (acc, variable) => {
        acc[variable.name] = variable.value;
        return acc;
      },
      {} as Record<string, any>
    );
    try {
      return {
        renderedContent: renderTemplate(content, {
          ...mockData,
          ...systemVariablesData,
        }),
        error: null,
      };
    } catch (error) {
      console.error('Error rendering template:', error);
      return { renderedContent: content, error: error as Error };
    }
  }, [content, mockData, systemVariables]);

  return (
    <PreviewContainer $height={height ?? 500}>
      {!!error && (
        <Alert
          message="Error happend while rendering template"
          description={error?.message}
          type="error"
          showIcon
        />
      )}
      {!error && <div dangerouslySetInnerHTML={{ __html: renderedContent }} />}
    </PreviewContainer>
  );
};

export const PreviewContainer = styled.div<{ $height: number }>`
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.white};
  min-height: ${({ $height }) => $height}px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 14px;
  line-height: 1.6;

  p {
    margin: 0.5em 0;
  }

  /* indent nested plain content (p tags) inside lists to match nested list indentation */
  ul > p,
  ol > p {
    margin-left: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.sm};
  }

  /* also handle p tags that follow list items (from nested plain loops) */
  ul li + p,
  ol li + p {
    margin-left: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    margin-top: ${({ theme }) => theme.spacing.sm};
  }

  .hidden {
    display: none;
  }
`;

export default TemplatePreview;
